// Source de vérité UNIQUE de l'état visible d'une capture (« phase »), partagée
// par l'accueil (CapturesList) ET le flux d'enregistrement (CaptureScreen).
//
// Avant, chaque surface avait sa propre table de libellés (STATUS_LABEL côté
// accueil, SYNC_LABEL/TRANSCRIBE_LABEL côté flux, EXTRACTION_BADGE en plus) :
// une même capture pouvait afficher « transcription » ici et « en file » là,
// avec un clignotement sur les états éphémères. On unifie ici :
//
//   - `deriveCapturePhase` combine le statut DB (`captures.status`) ET l'état
//     d'extraction dérivé (deriveExtractionStatus) en UNE phase visible.
//   - `phaseBadge` est la SEULE table de libellés (texte seul, sans glyphe,
//     terme unique « items »).
//
// Décisions produit appliquées telles quelles :
//   1. Terme unique « items » (jamais « besoins »).
//   2. L'étape technique `processing` est rendue comme `transcribing` (on ne
//      montre JAMAIS « traitement » → supprime le flash le plus visible).
//   3. Aucun glyphe Unicode dans les libellés.
//   4. Anti-flash : `ready` + 0 item dans la SEULE fenêtre de grâce (ni batch
//      actif, ni items, ni échec tracé) reste sur la phase amont `transcribing`
//      plutôt que d'émettre « en file ». « en file » n'est émis que sur signal
//      positif (batch actif / reprise auto tracée).

import {
  deriveExtractionStatus,
  EXTRACTION_GRACE_S,
  type ExtractionState,
  type ExtractionStatusInput,
  secondsSinceLatest,
} from "./extraction-status";
import {
  incompleteMessage,
  isIncomplete,
  isMinorGap,
  MAX_PLAUSIBLE_ELAPSED_MS,
} from "./incomplete";
import type { Capture, CaptureStatus } from "./schema";
import { TAKE_COPY } from "./take-copy";

// Phases visibles. Couvre les états amont (recording/uploading/captured/
// transcribing) ET les états d'extraction terminaux (queued/empty/failed/
// stalled/ready). `error` = échec amont (transcription) sans transcript exploitable.
// `transcribing` fusionne le statut DB `transcribing` ET `processing` (étape
// technique cachée).
// `preparing`/`problem` : dernier recours serveur (spec anti-perte phase 1,
// §5.2.2) — `assembling` (le serveur assemble, rien à faire) et
// `assembly_failed` (C21 : jamais un échec muet, une nouvelle tentative reste
// possible). Statuts amont au même titre que recording/uploading/captured :
// aucune information d'extraction n'a de sens tant que l'audio n'est pas là.
export type CapturePhase =
  | "recording"
  | "uploading"
  | "preparing"
  | "problem"
  | "captured"
  | "transcribing"
  | "queued"
  | "empty"
  | "failed"
  | "stalled"
  | "ready"
  | "error";

// Entrée de `deriveCapturePhase` = les signaux de l'extraction (réutilisés tels
// quels — on COMPOSE deriveExtractionStatus, on ne le réécrit pas) PLUS la
// présence de la synthèse, qui n'est pas un signal d'extraction : depuis le
// découplage du lot 2, la synthèse est produite par le cron `reconcile-extraction`
// bien après la fin de l'extraction. Les mêmes champs alimentent les deux surfaces.
export interface CapturePhaseInput extends ExtractionStatusInput {
  /** Une synthèse existe-t-elle pour cette capture ?
   *  `false` = la chaîne n'a pas fini → on n'a pas le droit de dire « Prêt ».
   *  `undefined` = pas encore chargée : on ne conclut RIEN (comportement
   *  historique), pour ne pas rétrograder sur une ignorance passagère. */
  hasSynthesis?: boolean;
  /** Secondes écoulées depuis l'arrivée de l'item le plus récent (cf.
   *  `secondsSinceNewestItem`). Borne l'attente de la synthèse ; absent → pas
   *  de borne (comportement historique). */
  secondsSinceNewestItem?: number;
}

// Plafond de l'attente de la SYNTHÈSE, items déjà là (SIG-261002-082836). Le
// cron `reconcile-extraction` abandonne un étage après `MAX_STAGE_ATTEMPTS` (2)
// échecs par génération d'items, et ne l'écrit que dans `stage_attempts`,
// réservé au service_role : sans borne, la capture restait « En file » pour
// toujours, sans recours.
//
// Pourquoi 2 h et pas moins : le délai NORMAL est d'une dizaine de minutes (max
// mesuré 11 min), mais le cron ne fait qu'UNE synthèse par tic de 5 min, et
// seulement s'il lui reste son budget nominal (120 s sur les 135 de l'échéance
// du tic) : un tic qui a déjà audité ou ré-extrait n'en fait pas. Un arrivage
// groupé de N captures demande donc ≈ 1,5 N tics (audits par deux, puis une
// synthèse par tic) : 2 h couvrent seize captures d'un coup, plus les reprises
// — bien au-delà d'une journée de production (1 à 3 captures). Conclure trop
// tôt inviterait à relancer une synthèse déjà en route.
export const SYNTHESIS_STALL_S = 2 * 3600;

// Secondes écoulées depuis la création de l'item le plus récent. Les items sont
// purgés puis réinsérés à chaque extraction : le plus récent date donc la fin de
// l'extraction courante, d'où part l'attente d'audit + synthèse. `undefined`
// sans item daté.
export function secondsSinceNewestItem(
  items: readonly { createdAt?: string }[],
  now: number = Date.now(),
): number | undefined {
  return secondsSinceLatest(
    items.map((it) => it.createdAt),
    now,
  );
}

export type PhaseBadgeTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface PhaseBadge {
  label: string;
  tone: PhaseBadgeTone;
}

// Mappe un état d'extraction terminal (sur une capture `ready`) vers la phase
// visible correspondante. `pending`/`none` ne devraient pas survenir ici (ready
// = amont terminé, transcript présent) — repli défensif sur `ready`.
function phaseFromExtraction(state: ExtractionState): CapturePhase {
  switch (state) {
    case "extracted":
      return "ready";
    case "queued":
      return "queued";
    case "empty":
      return "empty";
    case "failed":
      return "failed";
    case "stalled":
      return "stalled";
    default:
      // none / pending : pas d'info d'extraction exploitable → la capture est
      // prête (transcript écrit), on retombe sur « prêt ».
      return "ready";
  }
}

// Y a-t-il un signal POSITIF que l'analyse côté serveur est réellement en cours
// (≠ simple fenêtre de grâce silencieuse) ? Batch actif, OU échec tracé que le
// cron de réconciliation va réessayer (reprise auto imminente). Sur ces signaux
// on assume « en file » ; sinon on reste sur la phase amont (anti-flash).
function hasPositiveQueueSignal(input: CapturePhaseInput): boolean {
  return input.hasActiveBatch || input.ingestRunFailed;
}

// Capture `ready` FRAÎCHE (dans la fenêtre de grâce post-ready). Pendant ces
// quelques secondes, status=ready est écrit avant que la ligne llm_batches
// existe → on ne conclut RIEN sur l'extraction (anti-flash).
function isFreshlyReady(input: CapturePhaseInput): boolean {
  return input.secondsSinceReady !== undefined && input.secondsSinceReady < EXTRACTION_GRACE_S;
}

// Dérive la phase visible UNIQUE. Combine le statut DB et l'état d'extraction.
export function deriveCapturePhase(input: CapturePhaseInput): CapturePhase {
  switch (input.captureStatus) {
    case "recording":
      return "recording";
    case "uploading":
      return "uploading";
    case "assembling":
      return "preparing";
    case "assembly_failed":
      return "problem";
    case "captured":
      return "captured";
    case "transcribing":
      return "transcribing";
    // Étape technique cachée (décision PO #2) : `processing` est affiché comme
    // la phase amont `transcribing`, jamais « traitement ».
    case "processing":
      return "transcribing";
    default:
      break;
  }

  // À partir d'ici : statut `ready` ou `error`. On délègue à la logique
  // d'extraction existante (file / grâce / itemsLoaded / échec) — réutilisée
  // comme brique interne, pas réécrite.
  const extractionState = deriveExtractionStatus(input);

  // `error` : si l'extraction a quelque chose à dire (transcript présent →
  // failed/empty/queued/stalled/extracted), elle prime ; sinon c'est un échec
  // amont pur → phase `error`.
  if (input.captureStatus === "error" && extractionState === "none") {
    return "error";
  }

  // Anti-flash (décision PO #4 / cœur de l'item) : sur une capture `ready`, si
  // l'extraction n'est `queued` QUE par la fenêtre de grâce (aucun signal
  // positif : ni batch, ni échec tracé), on reste sur la phase amont
  // `transcribing` au lieu d'émettre « en file ». Évite le clignotement
  // transcription→en file pendant les quelques secondes où status=ready est
  // écrit avant que la ligne llm_batches existe.
  if (extractionState === "queued" && !hasPositiveQueueSignal(input)) {
    return "transcribing";
  }

  // Même garde côté liste d'accueil : une capture FRAÎCHEMENT prête dont les
  // items ne sont pas encore chargés (captures avant items) classerait en
  // `extracted` par défaut (itemsLoaded=false → supposé sain). Sans signal
  // d'items réels ni d'analyse côté serveur, on reste sur `transcribing` tant
  // qu'on est dans la grâce → pas de flash « prêt » prématuré sur une capture
  // dont on ne connaît pas encore les items.
  if (
    extractionState === "extracted" &&
    input.itemCount === 0 &&
    input.itemsLoaded === false &&
    !hasPositiveQueueSignal(input) &&
    isFreshlyReady(input)
  ) {
    return "transcribing";
  }

  const phase = phaseFromExtraction(extractionState);

  // « Prêt » n'est dit qu'à la FIN de la chaîne, synthèse comprise (arbitrage
  // produit du 2026-09-22 : « ce que je souhaite c'est que prêt indique que tout
  // est fait »). Avant ce verrou, le même écran affichait « Prêt » (ton succès)
  // et « Analyse en cours » à quelques centimètres l'un de l'autre : le statut DB
  // `ready` ne signifie que « transcription + extraction faites », or depuis le
  // découplage du lot 2 la synthèse est rattrapée par le cron
  // `reconcile-extraction`, un étage par tic de 5 min — ~10 min d'attente
  // mesurée en prod (médiane 9,6 min sur 13 captures depuis le 2026-08-20).
  //
  // On ne crée AUCUN libellé : on retarde simplement l'entrée dans `ready` et on
  // laisse la capture sur l'état d'attente déjà existant (`queued` → « En file »).
  //
  // Seule la phase `ready` est concernée. `empty` (0 item, extraction réussie)
  // n'obtient JAMAIS de synthèse — vérifié en prod : les 3 captures `ready` sans
  // synthèse sur 57 ont toutes 0 item. La verrouiller la bloquerait à jamais sur
  // « En file ». Idem pour `stalled`/`failed`, qui disent déjà autre chose.
  //
  // Ce verrou a une borne (SIG-261002-082836) : le cron abandonne un étage après
  // deux échecs, sans rien écrire que le client puisse lire. Passé
  // `SYNTHESIS_STALL_S` après l'arrivée des items, la synthèse ne viendra plus
  // toute seule : « À reprendre », qui offre la relance, plutôt qu'un « En
  // file » sans fin et sans recours.
  if (phase === "ready" && input.hasSynthesis === false) {
    const overdue =
      input.secondsSinceNewestItem !== undefined &&
      input.secondsSinceNewestItem >= SYNTHESIS_STALL_S;
    return overdue ? "stalled" : "queued";
  }

  return phase;
}

// SEULE table de libellés + ton. Texte seul (aucun glyphe Unicode — décision
// PO #3, alignée sur les autres badges). Terme unique « items » (jamais
// « besoins » — décision PO #1). `ready` est rendu avec un compteur d'items
// dynamique côté surface ; son libellé sert de repli (« prêt »).
const PHASE_BADGE: Record<CapturePhase, PhaseBadge> = {
  recording: { label: "Enregistrement", tone: "danger" },
  uploading: { label: "Téléversement", tone: "neutral" },
  preparing: { label: TAKE_COPY.badgePreparing, tone: "info" },
  problem: { label: TAKE_COPY.badgeProblem, tone: "warning" },
  captured: { label: "Non analysée", tone: "neutral" },
  transcribing: { label: "Transcription…", tone: "info" },
  queued: { label: "En file", tone: "info" },
  empty: { label: "Aucun item détecté", tone: "success" },
  failed: { label: "Échec", tone: "danger" },
  stalled: { label: "À reprendre", tone: "warning" },
  ready: { label: "Prêt", tone: "success" },
  error: { label: "Erreur", tone: "danger" },
};

export function phaseBadge(phase: CapturePhase): PhaseBadge {
  return PHASE_BADGE[phase];
}

// Phases qui demandent une action de reprise (extraction calée/échouée). Aligné
// sur `shouldOfferRetryExtraction` côté extraction — exposé ici pour que les
// surfaces décident à partir de la phase UNIQUE.
export function phaseOffersRetryExtraction(phase: CapturePhase): boolean {
  return phase === "stalled" || phase === "failed";
}

// Le statut DB seul suffit-il à dériver la phase (pas besoin de sonder
// ai_runs/llm_batches) ? Vrai pour les états amont (recording/uploading/
// captured/transcribing/processing) — l'extraction n'a pas son mot à dire.
const UPSTREAM_ONLY: ReadonlySet<CaptureStatus> = new Set([
  "recording",
  "uploading",
  "assembling",
  "assembly_failed",
  "captured",
  "transcribing",
  "processing",
]);

export function phaseIsUpstreamOnly(status: CaptureStatus): boolean {
  return UPSTREAM_ONLY.has(status);
}

// « Enregistrement incomplet » (I2/C09, décision contrôleur) : pastille
// SUPPLÉMENTAIRE — jamais une phase à elle seule, elle peut cohabiter avec
// n'importe quel statut amont ou aval (une rencontre `ready` peut très bien
// être incomplète). Deux déclencheurs INDÉPENDANTS (l'un OU l'autre suffit) :
//   1. Le serveur a lui-même signalé de l'audio illisible (`missingAudioMs > 0`,
//      C09 — segment illisible au milieu).
//   2. La durée mesurée est sous le seuil « Incomplète » vis-à-vis de la durée
//      d'horloge (`isIncomplete`, C02/C28 — interruption ou micro qui cesse de
//      capter sans signal de fin).
export type CaptureIncompleteInput = Pick<Capture, "durationS" | "elapsedS" | "missingAudioMs">;

// `elapsedS` connu ET plausible (même garde que `isIncomplete`), en ms — sert
// UNIQUEMENT à choisir le total du message ci-dessous : `isIncomplete` fait
// déjà sa propre garde de plausibilité pour la détection elle-même.
function elapsedMsIfPlausible(elapsedS: number | null | undefined): number | null {
  if (elapsedS == null) return null;
  const ms = elapsedS * 1000;
  if (!Number.isFinite(ms) || ms <= 0 || ms > MAX_PLAUSIBLE_ELAPSED_MS) return null;
  return ms;
}

export function isCaptureIncomplete(c: CaptureIncompleteInput): boolean {
  const measuredMs = c.durationS * 1000;
  const elapsedMs = c.elapsedS == null ? null : c.elapsedS * 1000;
  return (c.missingAudioMs ?? 0) > 0 || isIncomplete(measuredMs, elapsedMs);
}

// Total affiché : le PLUS GRAND de la durée d'horloge (quand connue et
// plausible) et de la durée mesurée + l'audio manquant estimé par le serveur
// (correctif A7 #7). Un simple repli sur l'horloge seule collait le mensonge
// « On a 1 h 30 min sur 1 h 30 min » quand elapsedS == durationS mais que le
// serveur signale quand même un `missingAudioMs` (le déclencheur 1 d'
// `isCaptureIncomplete`, indépendant de l'écart horloge/mesure) : le total
// doit alors AU MOINS refléter ce que le serveur dit manquant.
// L'horloge ne compte que si elle a elle-même déclenché l'état (écart au-delà
// de max(60 s, 2 %)) : sous ce seuil, c'est une dérive tolérée, qui ne doit pas
// gonfler l'écart d'une coupure de 5 s (revue du 30-09).
function incompleteTotalMs(c: CaptureIncompleteInput): number {
  const measuredMs = c.durationS * 1000;
  const measuredPlusMissing = measuredMs + (c.missingAudioMs ?? 0);
  const elapsed = elapsedMsIfPlausible(c.elapsedS);
  if (elapsed == null || !isIncomplete(measuredMs, elapsed)) return measuredPlusMissing;
  return Math.max(elapsed, measuredPlusMissing);
}

export function captureIncompleteMessage(c: CaptureIncompleteInput): string {
  return incompleteMessage(c.durationS * 1000, incompleteTotalMs(c));
}

// Niveau de l'écart (lot du 30-09, SIG-260929-160530) : `minor` (< 30 s, une
// coupure de batterie) = mention sobre, ni pastille ni « Analyser quand même » ;
// `major` = bandeau « attention », pastille et confirmation avant l'analyse.
export type IncompleteLevel = "none" | "minor" | "major";

export function captureIncompleteLevel(c: CaptureIncompleteInput): IncompleteLevel {
  if (!isCaptureIncomplete(c)) return "none";
  return isMinorGap(c.durationS * 1000, incompleteTotalMs(c)) ? "minor" : "major";
}

export function incompleteBadge(c: CaptureIncompleteInput): PhaseBadge | null {
  if (captureIncompleteLevel(c) !== "major") return null;
  return { label: TAKE_COPY.badgeIncomplete, tone: "warning" };
}
